// "Remember this device" on the email sign-in page. Ticked: the Supabase
// session lives in localStorage (still signed in after the browser closes)
// and the email is pre-filled next time. Unticked: it lives in sessionStorage
// and ends when the browser closes -- right for a shared kitchen PC.
// Passwords are never stored.

type KeyValueStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

const REMEMBER_KEY = "kitchen-hub-remember-device";
const EMAIL_KEY = "kitchen-hub-remembered-email";

function safe<T>(read: () => T, fallback: T): T {
  try { return read(); } catch { return fallback; }
}

/** Defaults to remembered, which is how sign-in behaved before the choice existed. */
export function readRememberDevice(storage: KeyValueStorage | null): boolean {
  return safe(() => storage?.getItem(REMEMBER_KEY) !== "false", true);
}

export function writeRememberDevice(storage: KeyValueStorage | null, remember: boolean, email: string): void {
  safe(() => {
    storage?.setItem(REMEMBER_KEY, String(remember));
    if (remember && email.trim()) storage?.setItem(EMAIL_KEY, email.trim());
    else storage?.removeItem(EMAIL_KEY);
  }, undefined);
}

export function readRememberedEmail(storage: KeyValueStorage | null): string {
  return safe(() => storage?.getItem(EMAIL_KEY) ?? "", "");
}

/** Supabase auth storage that follows the remember choice: writes go to the
 * chosen store and are cleared from the other; reads check both. */
export function deviceAuthStorage(local: KeyValueStorage | null, session: KeyValueStorage | null): KeyValueStorage {
  const chosen = () => (readRememberDevice(local) ? local : session);
  const other = () => (readRememberDevice(local) ? session : local);
  return {
    getItem: (key) => safe(() => chosen()?.getItem(key) ?? other()?.getItem(key) ?? null, null),
    setItem: (key, value) => safe(() => { chosen()?.setItem(key, value); other()?.removeItem(key); }, undefined),
    removeItem: (key) => safe(() => { local?.removeItem(key); session?.removeItem(key); }, undefined),
  };
}
